class CookieBanner extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      activeClass: 'is-active',
      closingClass: 'is-closing',
      preferencesView: 'preferences-view'
    };
    
    this.banner = this.querySelector('.cookie-banner');
    this.mainView = this.querySelector('.cookie__main-view');
    this.preferencesView = this.querySelector('.cookie__preferences');
    this.eventListenersAdded = false;
    this.collapsibleListenersAdded = false;
    this.isReopenedBanner = false;
    
    // Cookie preferences state
    this.cookiePreferences = {
      required: true,
      personalization: false,
      marketing: false,
      analytics: false,
      enhanced_services: false  // IMPORTANT: Must be defined in constructor
    };

    // Track which services have been initialized
    this.initializedServices = {
      facebookPixel: false,
      microsoftUet: false,
      googleAnalytics: false
    };

    this.debug = false; // Set to true for debugging

    this.log('CookieBanner constructor called');

    // IMPORTANT: loadFeatures must be in constructor, BEFORE other methods
    if (this.dataset.testMode === 'true') {
      this.log('Test mode enabled');
      this.init();
      this.setupEventListeners();
    } else {
      // beforeInit runs exactly once, whichever of these gets there first.
      let started = false;
      const start = () => {
        if (started) return;
        started = true;
        this.beforeInit();
      };
      window.Shopify.loadFeatures([
        {
          name: 'consent-tracking-api',
          version: '0.1',
        }
      ],
      (error) => {
        if (error) {
          // Blocked or failed: still ask (beforeInit copes without the API).
          this.log('consent-tracking-api failed to load', error);
          start();
          return;
        }
        // The loader can report the feature as loaded before
        // consent-tracking-api.js has attached Shopify.customerPrivacy (most
        // fresh visits on a slow start); reading it then threw and the
        // banner never opened.
        this.whenCustomerPrivacy(start);
      });
      // Backstop for a loader that never calls back at all.
      setTimeout(start, 12000);
    }

    this.setupEventListeners();
  }

  // Run `callback` once Shopify.customerPrivacy exists (polls every 50ms).
  // If it never arrives (blocked by an extension, say), run it after 10s
  // anyway: beforeInit treats a missing API as "not answered yet", so the
  // banner still shows, which is the compliant default.
  whenCustomerPrivacy(callback) {
    let tries = 0;
    const check = () => {
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      if ((cp && typeof cp.getTrackingConsent === 'function') || ++tries > 200) {
        callback();
        return;
      }
      setTimeout(check, 50);
    };
    check();
  }

  // Helper method - Should be early in the class
  log(message, data = null) {
    if (this.debug) {
      console.log(`[CookieBanner] ${message}`, data || '');
    }
  }

  // ============================================
  // LIFECYCLE METHODS - Must come early
  // ============================================
  
  connectedCallback() {
    this.log('Connected to DOM');
    this.setupEventListeners();
    
    // NEW: Listen for consent changes - MUST be in connectedCallback
    this.onVisitorConsentCollected = this.handleVisitorConsentCollected.bind(this);
    document.addEventListener('visitorConsentCollected', this.onVisitorConsentCollected);
    
    if (Shopify.designMode) {
      this.onShopifySectionLoad = this.onSectionLoad.bind(this);
      this.onShopifySectionSelect = this.onSectionSelect.bind(this);
      this.onShopifySectionDeselect = this.onSectionDeselect.bind(this);
      document.addEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.addEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.addEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }

    // IMPORTANT: checkExistingConsent must run AFTER event listeners are set up
    this.checkExistingConsent();
  }
  
  disconnectedCallback() {
    this.log('Disconnected from DOM');
    
    // NEW: Clean up consent listener
    if (this.onVisitorConsentCollected) {
      document.removeEventListener('visitorConsentCollected', this.onVisitorConsentCollected);
    }
    
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.removeEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }
    this.removeEventListeners();
  }

  // ============================================
  // SHOPIFY API HELPER METHODS
  // Define these BEFORE they're used in other methods
  // ============================================

  // NEW: Get user region - Define early
  getUserRegion() {
    if (typeof window.Shopify !== 'undefined' && 
        window.Shopify.customerPrivacy && 
        typeof window.Shopify.customerPrivacy.getRegion === 'function') {
      return window.Shopify.customerPrivacy.getRegion();
    }
    return '';
  }

  // NEW: Get consent ID - Define early
  getConsentId() {
    if (typeof window.Shopify !== 'undefined' && 
        window.Shopify.customerPrivacy && 
        typeof window.Shopify.customerPrivacy.consentId === 'function') {
      return window.Shopify.customerPrivacy.consentId();
    }
    return null;
  }

  // NEW: Get current consent - Define early
  getCurrentVisitorConsent() {
    if (typeof window.Shopify !== 'undefined' && 
        window.Shopify.customerPrivacy && 
        typeof window.Shopify.customerPrivacy.currentVisitorConsent === 'function') {
      return window.Shopify.customerPrivacy.currentVisitorConsent();
    }
    return null;
  }

  // NEW: Check for GPC - Define early
  hasGlobalPrivacyControl() {
    if (navigator.globalPrivacyControl === true) {
      this.log('Global Privacy Control detected');
      return true;
    }
    return false;
  }

  // ============================================
  // INITIALIZATION METHODS
  // These call the helper methods above
  // ============================================

  // UPDATED: Now uses helper methods defined above
  checkExistingConsent() {
    try {
      // First check Shopify's API for current consent
      const apiConsent = this.getCurrentVisitorConsent();
      if (apiConsent) {
        this.log('Found API consent:', apiConsent);
        
        // Map Shopify's format to our preferences
        this.cookiePreferences.analytics = apiConsent.analytics === 'yes';
        this.cookiePreferences.marketing = apiConsent.marketing === 'yes';
        this.cookiePreferences.personalization = apiConsent.preferences === 'yes';
        this.cookiePreferences.enhanced_services = apiConsent.sale_of_data !== 'no';
        
        this.applyConsentSettings(this.hasTrackingConsent());
        return;
      }
      
      // Fallback to localStorage
      const saved = localStorage.getItem('cookiePreferences');
      const hasConsented = localStorage.getItem('cookieConsentGiven');
      
      if (saved && hasConsented) {
        this.cookiePreferences = { ...this.cookiePreferences, ...JSON.parse(saved) };
        this.log('Found existing consent in localStorage, applying settings');
        this.applyConsentSettings(this.hasTrackingConsent());
      }
    } catch (e) {
      this.log('Could not check existing consent', e);
    }
  }

  hasTrackingConsent() {
    return this.cookiePreferences.personalization || 
           this.cookiePreferences.marketing || 
           this.cookiePreferences.analytics ||
           this.cookiePreferences.enhanced_services;
  }
  
  onSectionLoad(event) {
    filterShopifyEvent(event, this, () => this.open.bind(this));
  }
  
  onSectionSelect(event) {
    filterShopifyEvent(event, this, this.open.bind(this));
  }
  
  onSectionDeselect(event) {
    filterShopifyEvent(event, this, this.close.bind(this));
  }

  // UPDATED: Now uses getUserRegion() and hasGlobalPrivacyControl()
  beforeInit() {
  this.log('Before init check');

  // localStorage takes precedence over Shopify's customerPrivacy API.
  // Reason: setTrackingConsent() is async and immediately followed by
  // close() — on cross-page navigation (and especially in incognito),
  // getTrackingConsent() can still return 'no_interaction' because the
  // server-side cookie hasn't landed yet, causing the banner to
  // re-show despite the user having clicked Accept. localStorage is
  // synchronous and per-device, which is exactly what we want for
  // "has this browser already consented?".
  try {
    if (localStorage.getItem('cookieConsentGiven') === 'true') {
      this.log('Banner skipped - localStorage shows prior consent');
      const saved = localStorage.getItem('cookiePreferences');
      if (saved) {
        try {
          this.cookiePreferences = { ...this.cookiePreferences, ...JSON.parse(saved) };
        } catch (e) { /* malformed saved prefs — fall through */ }
      }
      this.applyConsentSettings(this.hasTrackingConsent());
      return;
    }
  } catch (e) {
    // localStorage unavailable (private mode quotas etc.) — fall through
    // to Shopify API path.
    this.log('localStorage check failed, falling back to Shopify API', e);
  }

  const customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
  const userCanBeTracked = customerPrivacy ? customerPrivacy.userCanBeTracked() : false;
  const userTrackingConsent = customerPrivacy ? customerPrivacy.getTrackingConsent() : 'no_interaction';
  const userRegion = this.getUserRegion();
  const hasGPC = this.hasGlobalPrivacyControl();

  this.log('User can be tracked:', userCanBeTracked);
  this.log('User tracking consent:', userTrackingConsent);
  this.log('User region:', userRegion);
  this.log('GPC:', hasGPC);

  // If GPC is enabled, automatically opt out of data sale
  if (hasGPC) {
    this.log('GPC detected - automatically opting out of data sale');
    this.cookiePreferences.enhanced_services = false;
    this.savePreferences();
    this.applyConsentSettings(false);
  }

  // Show banner to ALL users globally (EEA, US, UK, everywhere)
  // ONLY requirement: they haven't interacted yet + not on cart page
  const isCartPage = window.location.pathname.match(/^(\/[a-z]{2}(-[A-Z]{2})?)?\/cart$/);

  if (userTrackingConsent === 'no_interaction' && !isCartPage) {
    this.log('Showing banner - user has not interacted yet');
    this.init();
    this.setupEventListeners();
  } else {
    this.log('Banner not shown:', {
      trackingConsent: userTrackingConsent,
      isCartPage: isCartPage
    });
  }
}

  init() {
    this.log('Initializing banner');
    if (Shopify && Shopify.designMode) {
      return;
    }

    this.loadSavedPreferences();

    const delay = parseInt(this.dataset.delay) || 0;
      setTimeout(function() {
      this.open();
      }.bind(this), delay * 1000);
  }

  // ============================================
  // EVENT HANDLING
  // ============================================

  setupEventListeners() {
    // Only set up once - don't remove and re-add
    if (this.eventListenersAdded) {
      this.log('Event listeners already added, skipping');
      return;
    }

    this.log('Setting up event listeners');

    // Store bound references so they can be properly removed later
    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleChange = this.handleChange.bind(this);

    this.addEventListener('click', this.boundHandleClick);
    this.addEventListener('change', this.boundHandleChange);

    // Setup collapsible descriptions (mobile only)
    this.setupCollapsibleDescriptions();

    this.eventListenersAdded = true;
    this.log('Event listeners added successfully');
  }

  // Collapsible descriptions for mobile.
  // One delegated listener on the element, held as a single bound function.
  // open(forceReopen) — the footer "Cookie Settings" path — resets
  // collapsibleListenersAdded and calls setup again; with an anonymous
  // listener that stacked a second copy, so every tap opened the description
  // and the copy closed it straight back ("the chevrons do nothing").
  // addEventListener ignores a function that is already registered, so the
  // shared reference makes repeat setups harmless whatever the flag says.
  // "Mobile" is whether the chevron is showing (CSS hides it >= 750px).
  setupCollapsibleDescriptions() {
    if (!this.boundToggleDescription) {
      this.boundToggleDescription = (e) => {
        const btn = e.target.closest && e.target.closest('.cookie__category-toggle-btn');
        if (!btn || !this.contains(btn)) return;
        const icon = btn.querySelector('.cookie__expand-icon');
        if (icon && getComputedStyle(icon).display === 'none') return; // desktop: always open

        e.preventDefault();
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';
        const desc = document.getElementById(btn.getAttribute('aria-controls'));
        if (desc) {
          btn.setAttribute('aria-expanded', String(!isExpanded));
          desc.setAttribute('aria-hidden', String(isExpanded));
        }
      };
    }
    this.addEventListener('click', this.boundToggleDescription);
    this.collapsibleListenersAdded = true;
    this.log('Collapsible description listener ensured');
  }

  // Update selection counter
  updateSelectionCounter() {
    const countEl = this.querySelector('.cookie__selection-count');
    const totalEl = this.querySelector('.cookie__selection-total');

    if (!countEl || !totalEl) return;

    const optionalCategories = ['personalization', 'marketing', 'analytics', 'enhanced_services'];
    let enabledCount = 0;

    optionalCategories.forEach(category => {
      if (this.cookiePreferences[category]) {
        enabledCount++;
      }
    });

    countEl.textContent = enabledCount;
    totalEl.textContent = optionalCategories.length;

    this.log('Updated selection counter:', { enabled: enabledCount, total: optionalCategories.length });
  }

  // Focus trap for preferences view
  setupFocusTrap() {
    if (!this.preferencesView) return;

    const focusableElements = this.preferencesView.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    this.preferencesView.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    });

    // Focus first element when preferences open
    firstElement.focus();
  }

  // Handle Escape key to close preferences
  handleKeydown(e) {
    if (e.key === 'Escape' && this.banner?.classList.contains(this.classes.preferencesView)) {
      this.showMainView();
    }
  }

  removeEventListeners() {
    if (this.eventListenersAdded && this.boundHandleClick && this.boundHandleChange) {
      this.removeEventListener('click', this.boundHandleClick);
      this.removeEventListener('change', this.boundHandleChange);
      this.eventListenersAdded = false;
      this.log('Event listeners removed');
    }
  }

  handleClick(event) {
    const target = event.target;
    const button = target.closest('button');
    const buttonName = button?.name || button?.getAttribute('name') || target.name || target.getAttribute('name');
    
    this.log('Click detected', { 
      target: target.tagName, 
      name: buttonName, 
      text: target.textContent?.trim().substring(0, 20),
      isButton: target.tagName === 'BUTTON',
      hasButton: !!button
    });

    if (!buttonName) {
      this.log('No button name found, ignoring click');
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.log(`Processing button: ${buttonName}`);
    this.onButtonClick(event, buttonName);
  }

  handleChange(event) {
    if (event.target.classList.contains('cookie-toggle')) {
      this.log('Toggle change detected', { category: event.target.dataset.category, checked: event.target.checked });
      this.onToggleChange(event);
    }
  }

  // NEW: Handle consent changes from other sources
  handleVisitorConsentCollected(event) {
    this.log('Visitor consent collected event received:', event.detail);
    
    if (event.detail) {
      this.cookiePreferences.marketing = event.detail.marketingAllowed || false;
      this.cookiePreferences.analytics = event.detail.analyticsAllowed || false;
      this.cookiePreferences.personalization = event.detail.preferencesAllowed || false;
      this.cookiePreferences.enhanced_services = event.detail.saleOfDataAllowed || false;
      
      this.savePreferences();
      this.updateToggleStates();
    }
  }

  // ============================================
  // UI CONTROL METHODS
  // ============================================

  open(forceReopen = false) {
    this.log('Opening banner', { forceReopen, isReopenedBanner: this.isReopenedBanner });
    
    if (!this.banner) {
      this.log('Banner element not found!');
      return;
    }

    if (forceReopen) {
      this.isReopenedBanner = true;
      this.log('Banner marked as manually reopened');
    }
    
    this.banner.classList.remove(this.classes.closingClass);
    this.banner.classList.remove(this.classes.preferencesView);

    // For reopened banners, force refresh event listeners to ensure they work
    if (forceReopen || this.isReopenedBanner) {
      this.log('Refreshing event listeners for reopened banner');
      // Remove old listeners first
      if (this.boundHandleClick) {
        this.removeEventListener('click', this.boundHandleClick);
      }
      if (this.boundHandleChange) {
        this.removeEventListener('change', this.boundHandleChange);
      }
      // Reset flags to allow fresh setup
      this.eventListenersAdded = false;
      this.collapsibleListenersAdded = false;
    }

    this.banner.classList.add(this.classes.activeClass);
    this.showMainView();

    // Set up event listeners (will only add if not already added)
    this.setupEventListeners();

    document.body.classList.add('cookie-banner-open');
    this.loadSavedPreferences();
  }

  close() {
    this.log('Closing banner');
    if (!this.banner) return;
    
    document.body.classList.remove('cookie-banner-open');
    this.banner.classList.add(this.classes.closingClass);

    setTimeout(() => {
      this.banner.classList.remove(this.classes.activeClass);
      this.banner.classList.remove(this.classes.closingClass);
      this.banner.classList.remove(this.classes.preferencesView);
    }, 500);
  }

  showPreferencesView() {
    this.log('Showing preferences view');
    if (!this.banner) return;

    this.banner.classList.add(this.classes.preferencesView);

    if (this.mainView) this.mainView.style.display = 'none';
    if (this.preferencesView) {
      this.preferencesView.classList.add('is-visible');
      this.preferencesView.style.display = 'block';
    }

    this.updateToggleStates();
    this.updateSelectionCounter();
    this.setupFocusTrap();

    // Add keyboard listener for Escape
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.boundHandleKeydown);
  }

  showMainView() {
    this.log('Showing main view');
    if (!this.banner) return;

    this.banner.classList.remove(this.classes.preferencesView);

    if (this.preferencesView) {
      this.preferencesView.classList.remove('is-visible');
      this.preferencesView.style.display = 'none';
    }
    if (this.mainView) this.mainView.style.display = 'block';

    // Remove keyboard listener
    if (this.boundHandleKeydown) {
      document.removeEventListener('keydown', this.boundHandleKeydown);
    }
  }

  updateToggleStates() {
    this.log('Updating toggle states', this.cookiePreferences);
    Object.keys(this.cookiePreferences).forEach(category => {
      const toggle = this.querySelector(`input[data-category="${category}"]`);
      if (toggle && !toggle.disabled) {
        toggle.checked = this.cookiePreferences[category];
        this.log(`Updated toggle ${category} to ${toggle.checked}`);
      }
    });
  }

  onToggleChange(event) {
    const category = event.target.dataset.category;
    const isChecked = event.target.checked;

    this.log(`Toggle changed: ${category} = ${isChecked}`);

    if (category && this.cookiePreferences.hasOwnProperty(category)) {
      this.cookiePreferences[category] = isChecked;
      this.updateSelectionCounter();
    }
  }

  // ============================================
  // PREFERENCE MANAGEMENT
  // ============================================

  loadSavedPreferences() {
    try {
      const saved = localStorage.getItem('cookiePreferences');
      if (saved) {
        this.cookiePreferences = { ...this.cookiePreferences, ...JSON.parse(saved) };
        this.log('Loaded saved preferences', this.cookiePreferences);
      }
    } catch (e) {
      this.log('Could not load saved cookie preferences', e);
    }
  }

  // UPDATED: Now saves consent ID
  savePreferences() {
    try {
      localStorage.setItem('cookiePreferences', JSON.stringify(this.cookiePreferences));
      localStorage.setItem('cookieConsentGiven', 'true');
      
      // Store consent ID for reference
      const consentId = this.getConsentId();
      if (consentId) {
        localStorage.setItem('cookieConsentId', consentId);
        this.log('Saved preferences with consent ID:', consentId);
      }
      
      this.log('Saved preferences', this.cookiePreferences);
    } catch (e) {
      this.log('Could not save cookie preferences', e);
    }
  }

  acceptAllCookies() {
    this.log('Accepting all cookies');
    this.cookiePreferences = {
      required: true,
      personalization: true,
      marketing: true,
      analytics: true,
      enhanced_services: true
    };
    this.savePreferences();
    this.applyConsentSettings(true);
  }

  declineAllCookies() {
    this.log('Declining all cookies');
    this.cookiePreferences = {
      required: true,
      personalization: false,
      marketing: false,
      analytics: false,
      enhanced_services: false
    };
    this.savePreferences();
    this.applyConsentSettings(false);
  }

  saveCustomPreferences() {
    this.log('Saving custom preferences');
    this.savePreferences();
    
    const hasTrackingCookies = this.hasTrackingConsent();
    this.applyConsentSettings(hasTrackingCookies);
  }

  // ============================================
  // CONSENT APPLICATION
  // IMPORTANT: This must come AFTER all helper methods are defined
  // ============================================

  // UPDATED: Uses getConsentId() and getUserRegion()
  applyConsentSettings(allowTracking) {
    this.log('Applying consent settings', { allowTracking, preferences: this.cookiePreferences });
    
    if (typeof window.Shopify !== 'undefined' && window.Shopify.customerPrivacy) {
      try {
        const consentPayload = {
          analytics: this.cookiePreferences.analytics,
          marketing: this.cookiePreferences.marketing,
          preferences: this.cookiePreferences.personalization,
          sale_of_data: this.cookiePreferences.enhanced_services,
          // OPTIONAL: Add custom metafields
          metafields: [
            { key: "banner_version", value: "2.0" },
            { key: "consent_method", value: this.isReopenedBanner ? "manual" : "banner" },
            { key: "gpc_detected", value: this.hasGlobalPrivacyControl() ? "true" : "false" }
          ]
        };
        
        this.log('Setting tracking consent with payload:', consentPayload);
        window.Shopify.customerPrivacy.setTrackingConsent(consentPayload, () => {
          this.log('Tracking consent set successfully');
        });
      } catch (error) {
        this.log('Error setting tracking consent:', error);
        console.error('Cookie banner error:', error);
      }
    } else {
      this.log('Shopify customerPrivacy API not available');
    }

    try {
      this.updateGoogleConsent();
      this.updateMicrosoftConsent();
    } catch (error) {
      this.log('Error updating third-party consent:', error);
    }
    
    try {
      this.initializeTrackingServices();
    } catch (error) {
      this.log('Error initializing tracking services:', error);
    }

    try {
      const eventType = allowTracking ? 'trackingConsentAccepted' : 'trackingConsentDeclined';
      document.dispatchEvent(new CustomEvent(eventType, {
        detail: { 
          preferences: this.cookiePreferences,
          enhancedServices: this.cookiePreferences.enhanced_services,
          consentId: this.getConsentId(),
          region: this.getUserRegion()
        }
      }));
      this.log('Dispatched event:', eventType);
    } catch (error) {
      this.log('Error dispatching event:', error);
    }
  }

  updateGoogleConsent() {
    // Google Consent Mode v2 - includes ad_user_data and ad_personalization
    const consentState = {
      'ad_storage': this.cookiePreferences.marketing ? 'granted' : 'denied',
      'ad_user_data': this.cookiePreferences.marketing ? 'granted' : 'denied',
      'ad_personalization': this.cookiePreferences.marketing ? 'granted' : 'denied',
      'analytics_storage': this.cookiePreferences.analytics ? 'granted' : 'denied',
      'personalization_storage': this.cookiePreferences.personalization ? 'granted' : 'denied',
      'functionality_storage': this.cookiePreferences.required ? 'granted' : 'denied'
    };

    // Primary: Update via Cloudflare Zaraz
    if (typeof zaraz !== 'undefined' && typeof zaraz.set === 'function') {
      zaraz.set('google_consent_update', consentState);
      this.log('Updated Google Consent Mode v2 via Zaraz:', consentState);

      // Pinterest (and any non-Google Zaraz tool) does NOT read Google Consent
      // Mode, so expose plain Zaraz variables for a blocking trigger to gate on.
      // Pinterest tool in Zaraz is set to fire only when consent_marketing=granted.
      zaraz.set('consent_marketing', this.cookiePreferences.marketing ? 'granted' : 'denied');
      zaraz.set('consent_analytics', this.cookiePreferences.analytics ? 'granted' : 'denied');
      this.log('Set Zaraz consent variables for non-Consent-Mode tools');
    }

    // Fallback: Update via gtag (for non-Zaraz tools or if Zaraz not loaded)
    if (typeof gtag === 'function') {
      gtag('consent', 'update', consentState);
      this.log('Updated Google Consent Mode v2 via gtag:', consentState);
    }
  }

  updateMicrosoftConsent() {
    // Zaraz handles Microsoft UET but may read consent from uetq
    window.uetq = window.uetq || [];
    window.uetq.push('consent', 'update', {
      'ad_storage': this.cookiePreferences.marketing ? 'granted' : 'denied'
    });
    this.log('Updated Microsoft UET consent settings (Zaraz reads this)');
  }

  // NOTE: Tracking services (Facebook, Microsoft UET, Google Analytics) are now
  // handled by Cloudflare Zaraz. Zaraz reads consent state from gtag and uetq.
  initializeTrackingServices() {
    // Check if tracking is allowed before initializing any service
    if (typeof window.Shopify !== 'undefined' &&
        window.Shopify.customerPrivacy &&
        !window.Shopify.customerPrivacy.userCanBeTracked()) {
      this.log('Tracking not allowed, skipping service initialization');
      return;
    }

    // Facebook, Microsoft UET, Google Analytics now handled by Zaraz
    this.log('Other tracking services handled by Cloudflare Zaraz');
  }

  // ============================================
  // BUTTON HANDLERS
  // ============================================

  onButtonClick(event, buttonName) {
    this.log(`Button clicked: ${buttonName}`, { isReopenedBanner: this.isReopenedBanner });

    // Visual feedback
    const button = event.target.closest('button');
    if (button) {
      button.style.transform = 'scale(0.95)';
      button.style.transition = 'transform 0.1s ease';
      setTimeout(() => {
        button.style.transform = '';
      }, 150);
    }

    switch (buttonName) {
      case 'accept':
        this.log('Processing accept');
        this.acceptAllCookies();
        this.close();
        this.showSuccessFeedback('All cookies accepted');
        break;

      case 'decline':
        this.log('Processing decline');
        this.declineAllCookies();
        this.close();
        this.showSuccessFeedback('Only essential cookies enabled');
        break;

      case 'preferences':
        this.log('Processing preferences');
        this.showPreferencesView();
        break;

      case 'close-preferences':
        this.log('Processing close preferences');
        this.showMainView();
        break;

      case 'save-preferences':
        this.log('Processing save preferences');
        this.saveCustomPreferences();
        this.close();
        this.showSuccessFeedback('Preferences saved');
        break;

      case 'accept-all-preferences':
        this.log('Processing accept all preferences');
        this.acceptAllCookies();
        this.close();
        this.showSuccessFeedback('All cookies accepted');
        break;

      case 'decline-all-preferences':
        this.log('Processing decline all preferences');
        this.declineAllCookies();
        this.close();
        this.showSuccessFeedback('Only essential cookies enabled');
        break;

      default:
        this.log(`Unknown button: ${buttonName}`);
        this.close();
    }
  }

  showSuccessFeedback(message) {
    this.log(`Showing feedback: ${message}`);
    const feedback = document.createElement('div');
    feedback.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #287d39;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
      z-index: 9999999;
      font-size: 14px;
      font-weight: 500;
      transform: translateX(100%);
      transition: transform 0.3s ease;
    `;
    feedback.textContent = `✓ ${message}`;
    
    document.body.appendChild(feedback);
    
    setTimeout(() => feedback.style.transform = 'translateX(0)', 10);
    
    setTimeout(() => {
      feedback.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (feedback.parentNode) {
          feedback.parentNode.removeChild(feedback);
        }
      }, 300);
    }, 3000);
  }

  noop() {}
}

customElements.define('cookie-banner', CookieBanner);